import ExpandableContainer from '../../../../../components/ExpandableContainer';
import { Table } from '../Table';
import TransformationSidebar from '../TransformationSidebar';
import { memo, useEffect, useMemo } from 'react';
import { generateAlphabetColumn, getColumns } from '../Extraction';
import { useHook } from '../hooks/useHook';
import { Box, LoadingOverlay, Flex } from '@mantine/core';
import { createStyles } from '@mantine/emotion';
import extractionStore from '../../../store';
import { observer } from 'mobx-react-lite';
import { SPECIAL_NULL_STRING } from '../../../../../components/CanvasTable/constants';
import { CellWrapper } from './style';

const useStyles = createStyles(theme => ({
  table: {
    backgroundColor: '#fff',
    borderRadius: '4px',
    flex: 1
  }
}));

const Transformation = observer(({ context }) => {
  const {
    templateType,
    extractId,
    width,
    setWidth,
    periodId,
    workflowId,
    rowPinning,
    columnPinning,
    changeActiveTab,
    term,
    zoomLevel,
    isStructured,
    activeTab,
    isLoading,
    transformationData,
    setTransformationData,
    foundCell,
    headerComponent,
    onFocusedCell,
    year,
    workflowName,
    periodName,
    extractedTransformationTable,
    onChangeExtractedTransformationTable,
    onFixRow,
    onFixColumn,
    transformationProgressStatus,
    canManageWorkflow
  } = context;

  const {
    stepIn,
    columns,
    amongIndex,
    isFetching,
    isOriginalView,
    isLoading: isLoadingTransformations,
    transformations,
    tranformationId,
    isLoadingTransformationPatch,
    columnsTypes,
    setIsNewAction,
    toggleStepIn,
    setAmongIndex,
    addTransformation,
    editTransformation,
    toggleOriginalView,
    setTransformations,
    removeTransformation,
    handleSaveTransformations,
    refetch
  } = useHook({
    activeTab,
    extractId,
    setTransformationData,
    extractedTransformationTable,
    onChangeExtractedTransformationTable
  });

  const maxExpandedWidth = window.innerWidth * 0.94;

  const { isSidebarExpanded, isLockedPeriod } = extractionStore;

  useEffect(() => {
    return () => {
      setTransformationData([]);
    };
  }, []);

  const rows = useMemo(() => {
    return transformationData?.map((item, index) => {
      const newItem = {
        1: `${index + 1}`
      };

      Object.values(item).forEach((value, i) => {
        newItem[i + 2] = value;
      });

      return newItem;
    });
  }, [transformationData]);

  useEffect(() => {
    if (rows?.length > 5) {
      try {
        // for Transformation the first row will always be the header row and we can freeze it
        onFixRow(1);
        onFixColumn(null);
      } catch (error) {
        console.log('Failed to fix first row in Transformation', error);
      }
    }
  }, [rows]);

  const rowCount = useMemo(() => {
    if (!transformationData || !transformationData?.[0]) return 0;
    return Math.round((window.innerWidth - 460) / 100) -
      Object.keys(transformationData?.[0])?.length >
      0
      ? Math.round((window.innerWidth - 460) / 100) - Object.keys(transformationData?.[0]).length
      : 0;
  }, [transformationData]);

  const _columns = useMemo(() => {
    const result = getColumns(transformationData?.[0], rowCount)?.map(column => ({
      ...column,
      accessorKey: `${column.columnId}`,
      header: generateAlphabetColumn(column.columnId - 1),
      size: column.width,
      cell: ({ row, getValue, column }) => {
        const value = getValue(row.index);
        const rowId = row.index + 1;

        return <CellWrapper>{value}</CellWrapper>;
      }
    }));

    return [
      {
        columnId: 1,
        accessorKey: `1`,
        header: '№',
        size: 40
      },
      ...result
    ];
  }, [transformationData, rowCount]);

  const transformationDataWithNullCells = useMemo(() => {
    if (!transformationData || !Array.isArray(transformationData)) return null;
    return transformationData.map((tObj, idx) => {
      const updatedObj = {};

      for (const key in tObj) {
        // if value is undefined or null we should display 'null' in the table
        // here special 'null\t' is used to differentiate in case we receive regular 'null' text as a value
        updatedObj[key] = tObj[key] ?? SPECIAL_NULL_STRING;
      }
      return updatedObj;
    });
  }, [transformationData]);

  const { classes } = useStyles();

  const disableAddAction =
    transformationProgressStatus === 'Not started' && templateType === 'from template';

  return (
    <Flex id="grid-table-transformation-root" gap="12px" w="100%">
      <ExpandableContainer
        id="grid-table-transformation-sidebar-container"
        width={isSidebarExpanded ? maxExpandedWidth : width}
        setWidth={setWidth}
        initWidth={530}
        maxWidth={maxExpandedWidth}
      >
        <TransformationSidebar
          disableAddAction={disableAddAction}
          workflowId={workflowId}
          periodId={periodId}
          amongIndex={amongIndex}
          isOriginalView={isOriginalView}
          toggleOriginalView={toggleOriginalView}
          setAmongIndex={setAmongIndex}
          stepIn={stepIn}
          tranformationId={tranformationId}
          isFetching={isFetching}
          columns={columns}
          isLoadingTransformations={isLoadingTransformations}
          transformations={transformations}
          tableName={extractedTransformationTable}
          isLoadingTransformationPatch={isLoadingTransformationPatch}
          addTransformation={addTransformation}
          toggleStepIn={toggleStepIn}
          transformationGarbageCollector={() => setTransformationData()}
          editTransformation={editTransformation}
          removeTransformation={removeTransformation}
          setTransformations={setTransformations}
          extractId={extractId}
          handleSaveTransformations={handleSaveTransformations}
          width={isSidebarExpanded ? maxExpandedWidth : width}
          year={year}
          setIsNewAction={setIsNewAction}
          workflowName={workflowName}
          periodName={periodName}
          refetch={refetch}
          isLockedPeriod={isLockedPeriod}
          changeActiveTab={changeActiveTab}
          canManageWorkflow={canManageWorkflow}
        />
      </ExpandableContainer>

      <Box
        id="grid-table-transformation-table-shell"
        p="16px"
        sx={{ flex: 1, height: '100%', width: `calc(100% - ${width}px)` }}
        pos="relative,"
        className={classes.table}
      >
        <LoadingOverlay id="grid-table-transformation-loading" visible={isLoading} zIndex={1000} />
        {!isLoading && transformationData && transformationDataWithNullCells?.length > 0 && (
          <>
            {headerComponent}
            <Table
              rowPinning={rowPinning}
              columnPinning={columnPinning}
              rows={rows}
              term={term}
              zoomLevel={zoomLevel}
              isStructured={isStructured}
              columns={_columns}
              isTransformation={true}
              foundCell={foundCell}
              data={transformationDataWithNullCells}
              onFocusedCell={onFocusedCell}
              handleSelection={() => {}}
              columnsTypes={columnsTypes}
              sx={{ width: '100%' }}
            />
          </>
        )}
      </Box>
    </Flex>
  );
});

export default memo(Transformation);
